import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchFromApi } from 'api/apiClient';
import { useUI } from 'contexts/UiContext';
import { SortOrder } from 'hooks/useTableControls';
import {
  CountResponse,
  CustomProcessField,
  ProcessField,
  ProcessForm,
} from 'types/dataTableTypes';
import { PaginationQueryParams } from 'types/queryTypes';

// Base API URL
const REACT_APP_DARWIN_URL = import.meta.env.VITE_DARWIN_URL;

// Query keys
const DATA_TABLE_FIELDS_QUERY_KEY = 'data-table-fields';
const DATA_TABLE_CUSTOM_FIELD_COUNT_QUERY_KEY = 'data-table-custom-field-count';
const DATA_TABLE_CUSTOM_FIELDS_WITH_FORM_COUNT_QUERY_KEY =
  'data-table-custom-fields-with-form-count';
const FORMS_BY_PROCESS_ID_QUERY_KEY = 'forms-by-process-id';

/**
 * Fetch data table fields.
 */
export const useGetProcessFields = ({
  page = 1,
  limit = 10,
  query = '',
  sortOrder = { sortBy: 'name', order: 'ASC' },
  processId = '',
}: PaginationQueryParams & {
  sortOrder: SortOrder;
  processId: string;
  formId?: string;
}) => {
  const { selectedCompany } = useUI();

  return useQuery({
    queryKey: [
      DATA_TABLE_FIELDS_QUERY_KEY,
      selectedCompany,
      processId,
      page,
      limit,
      query,
      sortOrder,
    ],
    enabled: !!processId,
    queryFn: () =>
      fetchFromApi<ProcessField[]>(
        `${REACT_APP_DARWIN_URL}/processes/${selectedCompany}/fields/${processId}?limit=${limit}&page=${page}&filter=${query}&order=${
          sortOrder.order
        }${sortOrder.sortBy ? `&sortBy=${sortOrder.sortBy}` : ''}`
      ),
  });
};

/**
 * Create a new process field for a specific process.
 */
export const useCreateProcessField = () => {
  const queryClient = useQueryClient();
  const { selectedCompany } = useUI();

  return useMutation({
    mutationFn: async ({
      processId,
      newField,
    }: {
      processId: string;
      newField: Omit<ProcessField, 'id'>;
    }) => {
      return fetchFromApi<ProcessField>(
        `${REACT_APP_DARWIN_URL}/processes/${selectedCompany}/${processId}/fields`,
        {
          method: 'POST',
          body: JSON.stringify(newField),
        }
      );
    },
    onSuccess: (_, { processId }) => {
      queryClient.invalidateQueries({
        queryKey: [DATA_TABLE_FIELDS_QUERY_KEY, selectedCompany, processId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          DATA_TABLE_CUSTOM_FIELDS_WITH_FORM_COUNT_QUERY_KEY,
          selectedCompany,
          processId,
        ],
      });
    },
  });
};

/**
 * Edit a process field for a specific process.
 */
export const useEditProcessField = () => {
  const queryClient = useQueryClient();
  const { selectedCompany } = useUI();

  return useMutation({
    mutationFn: async ({
      processId,
      newField,
      fieldId,
    }: {
      processId: string;
      newField: Omit<ProcessField, 'id'>;
      fieldId: string;
    }) => {
      return fetchFromApi<ProcessField>(
        `${REACT_APP_DARWIN_URL}/processes/${selectedCompany}/fields/${fieldId}`,
        {
          method: 'PUT',
          body: JSON.stringify(newField),
        }
      );
    },
    onSuccess: (_, { processId }) => {
      queryClient.invalidateQueries({
        queryKey: [DATA_TABLE_FIELDS_QUERY_KEY, selectedCompany, processId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          DATA_TABLE_CUSTOM_FIELDS_WITH_FORM_COUNT_QUERY_KEY,
          selectedCompany,
          processId,
        ],
      });
    },
  });
};

/**
 * Delete a custom field.
 */
export const useDeleteProcessField = () => {
  const queryClient = useQueryClient();
  const { selectedCompany } = useUI();

  return useMutation({
    mutationFn: async ({
      processId,
      fieldId,
    }: {
      processId: string;
      fieldId: string;
    }) => {
      return fetchFromApi<ProcessField>(
        `${REACT_APP_DARWIN_URL}/processes/${selectedCompany}/fields/${fieldId}`,
        {
          method: 'DELETE',
        }
      );
    },
    onSuccess: (_, { processId }) => {
      queryClient.invalidateQueries({
        queryKey: [DATA_TABLE_FIELDS_QUERY_KEY, selectedCompany, processId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          DATA_TABLE_CUSTOM_FIELDS_WITH_FORM_COUNT_QUERY_KEY,
          selectedCompany,
          processId,
        ],
      });
    },
  });
};

/**
 * Fetch forms by process ID.
 */
export const useGetFormsByProcessId = ({
  processId,
}: {
  processId: string;
}) => {
  const { selectedCompany } = useUI();

  return useQuery({
    queryKey: [FORMS_BY_PROCESS_ID_QUERY_KEY, selectedCompany, processId],
    enabled: !!processId,
    queryFn: () =>
      fetchFromApi<ProcessForm[]>(
        `${REACT_APP_DARWIN_URL}/processes/${selectedCompany}/${processId}/forms`
      ),
  });
};

/**
 * Get count of data table custom field.
 */
export const useGetCountDataTableCustomField = (fieldId: string) => {
  const { selectedCompany } = useUI();
  return useQuery({
    queryKey: [
      DATA_TABLE_CUSTOM_FIELD_COUNT_QUERY_KEY,
      selectedCompany,
      fieldId,
    ],
    queryFn: () =>
      fetchFromApi<CountResponse>(
        `${REACT_APP_DARWIN_URL}/processes/${selectedCompany}/fields/${fieldId}/forms`
      ),
  });
};

/**
 * Fetch custom fields with form count for a specific process.
 */
export const useGetCustomFieldsWithFormCount = ({
  page = 1,
  limit = 10,
  query = '',
  sortOrder = { sortBy: 'name', order: 'ASC' },
  processId = '',
  formId,
}: PaginationQueryParams & {
  sortOrder: SortOrder;
  processId: string;
  formId?: string;
}) => {
  const { selectedCompany } = useUI();

  return useQuery({
    queryKey: [
      DATA_TABLE_CUSTOM_FIELDS_WITH_FORM_COUNT_QUERY_KEY,
      selectedCompany,
      processId,
      page,
      limit,
      query,
      sortOrder,
      formId,
    ],
    enabled: !!processId,
    queryFn: () =>
      fetchFromApi<CustomProcessField[]>(
        `${REACT_APP_DARWIN_URL}/processes/${selectedCompany}/custom-fields/${processId}?limit=${limit}&page=${page}&filter=${query}&order=${
          sortOrder.order
        }${sortOrder.sortBy ? `&sortBy=${sortOrder.sortBy}` : ''}${
          formId ? `&formId=${formId}` : ''
        }`
      ),
  });
};
